import React, { useState } from 'react';
import { FileText, X } from 'lucide-react';

interface ReceiptItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface Transaction {
  id?: number;
  timestamp?: string;
  items: ReceiptItem[];
  subtotal: number;
  tax: number;
  total: number;
  payment_type: 'cash' | 'paypay';
  received?: number;
  change?: number;
  order_type: 'eatin' | 'takeout';
  tax_mode?: 'internal' | 'external' | 'exempt';
  tax_rate?: number;
  is_tax_exempt?: boolean;
}

interface ReceiptModalProps {
  transaction: Transaction;
  onClose: () => void;
  isOpen: boolean;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({
  transaction,
  onClose,
  isOpen
}) => {
  const [customerName, setCustomerName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);

  if (!isOpen) return null;

  const formatDateTime = (timestamp?: string) => {
    const date = timestamp ? new Date(timestamp) : new Date();
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) {
      return '¥0';
    }
    return `¥${amount.toLocaleString()}`;
  };

  // 縦型領収書（品目あり）の印刷
  const handlePrintReceipt = () => {
    console.log('領収書印刷開始（縦型・品目あり）');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      console.error('ポップアップがブロックされました');
      alert('ポップアップがブロックされています。ブラウザの設定を確認してください。');
      return;
    }

    console.log('印刷ウィンドウ opened');
    printWindow.document.write('<!doctype html><html><head><meta charset="utf-8"><title>印刷準備中</title></head><body>読み込み中…</body></html>');
    printWindow.document.close();

    const receiptContent = generateReceiptHTML();
    printWindow.document.open();
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    onClose();
  };

  // 横型領収書（品目なし）の印刷
  const handlePrintHorizontalReceipt = () => {
    console.log('領収書印刷開始（横型・品目なし）');
    const receiptContent = generateHorizontalReceiptHTML();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      console.log('印刷ウィンドウ opened');
      printWindow.document.write(receiptContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 100);
    } else {
      console.error('ポップアップがブロックされました');
      alert('ポップアップがブロックされています。ブラウザの設定を確認してください。');
    }
    onClose();
  };

  // 縦型領収書（品目あり） - 現在のバージョン
  const generateReceiptHTML = () => {
    const receiptDate = transaction.timestamp ? new Date(transaction.timestamp) : new Date();
    const receiptNumber = transaction.id?.toString().padStart(8, '0') || '00000000';
    
    return `
<!DOCTYPE html>
<html lang="ja">
<head>
    <meta charset="UTF-8">
    <title>領収書 - とんかつ和とん</title>
    <style>
        @page {
            margin: 0;
        }
        body {
            font-family: 'MS Gothic', monospace;
            margin: 0;
            padding: 10mm 5mm;
            width: 70mm;
        }
        .receipt {
            background: white;
            padding: 5mm;
            border: 2px solid #000;
        }
        .header {
            text-align: center;
            margin-bottom: 5mm;
        }
        .title {
            font-size: 20pt;
            font-weight: bold;
            margin: 5mm 0;
            letter-spacing: 3mm;
        }
        .shop-info {
            font-size: 9pt;
            margin: 2mm 0;
        }
        .customer-section {
            margin: 8mm 0;
            padding: 3mm 0;
            border-top: 1px solid #000;
            border-bottom: 1px solid #000;
        }
        .customer-name {
            font-size: 12pt;
            text-align: center;
            margin-bottom: 3mm;
        }
        .amount-section {
            margin: 8mm 0;
            text-align: center;
        }
        .amount-label {
            font-size: 10pt;
            margin-bottom: 2mm;
        }
        .amount {
            font-size: 18pt;
            font-weight: bold;
            border-bottom: 3px double #000;
            padding-bottom: 2mm;
            display: inline-block;
            min-width: 50mm;
        }
        .details {
            margin: 5mm 0;
            font-size: 9pt;
        }
        .detail-row {
            display: flex;
            justify-content: space-between;
            margin: 1mm 0;
            padding: 1mm 0;
        }
        .item-name {
            flex: 1;
        }
        .item-qty {
            margin: 0 5mm;
        }
        .item-amount {
            text-align: right;
            min-width: 20mm;
        }
        .subtotal-section {
            border-top: 1px solid #000;
            margin-top: 3mm;
            padding-top: 3mm;
        }
        .total-row {
            display: flex;
            justify-content: space-between;
            font-weight: bold;
            font-size: 11pt;
            margin-top: 3mm;
            padding-top: 3mm;
            border-top: 2px solid #000;
        }
        .stamp-section {
            margin-top: 10mm;
            display: flex;
            justify-content: flex-end;
        }
        .stamp-box {
            width: 20mm;
            height: 20mm;
            border: 1px solid #000;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8pt;
        }
        .footer {
            margin-top: 5mm;
            padding-top: 3mm;
            border-top: 1px dashed #000;
            font-size: 8pt;
            text-align: center;
        }
        .receipt-info {
            font-size: 8pt;
            margin-top: 3mm;
        }
        @media print {
            body {
                width: 80mm !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            .receipt {
                width: 70mm !important;
                margin: 0 !important;
                box-sizing: border-box !important;
            }
        }
    </style>
</head>
<body>
    <div class="receipt">
        <div class="header">
            <div class="title">領　収　書</div>
        </div>
        
        <div class="customer-section">
            <div class="customer-name">
                ${customerName ? customerName + ' 様' : '　　　　　　　　　　様'}
            </div>
        </div>
        
        <div class="amount-section">
            <div class="amount-label">金額</div>
            <div class="amount">${formatCurrency((transaction as any).final_total || transaction.total)}</div>
        </div>
        
        <div class="details">
            <div class="detail-row">
                <span>但し、お食事代として</span>
            </div>
        </div>
        
        <div class="details">
            ${(transaction.items || []).map(item => `
                <div class="detail-row">
                    <span class="item-name">${item.name}</span>
                    <span class="item-qty">×${item.quantity}</span>
                    <span class="item-amount">${formatCurrency(item.price * item.quantity)}</span>
                </div>
            `).join('')}
            
            <div class="subtotal-section">
                <div class="detail-row">
                    <span>小計</span>
                    <span class="item-amount">${formatCurrency(transaction.subtotal)}</span>
                </div>
                ${transaction.tax_mode !== 'exempt' && !transaction.is_tax_exempt ? `
                <div class="detail-row">
                    <span>${transaction.tax_mode === 'external' ? `消費税(${transaction.tax_rate || 10}%)` : `(内消費税 ${transaction.tax_rate || 10}%)`}</span>
                    <span class="item-amount">${formatCurrency(transaction.tax)}</span>
                </div>` : ''}
                ${(transaction as any).discount_amount && (transaction as any).discount_amount > 0 ? `
                <div class="detail-row" style="margin-top: 3mm; color: #d00;">
                    <span>値引き${(transaction as any).coupon_discount > 0 ? '（クーポン）' : (transaction as any).manual_discount > 0 ? '（店舗）' : ''}</span>
                    <span class="item-amount">-${formatCurrency((transaction as any).discount_amount)}</span>
                </div>` : ''}
            </div>

            <div class="total-row" style="margin-top: 5mm; font-size: 13pt;">
                <span>お支払い額</span>
                <span>${formatCurrency((transaction as any).final_total || transaction.total)}</span>
            </div>
        </div>
        
        <div class="stamp-section">
            <div class="stamp-box">印</div>
        </div>
        
        <div class="footer">
            <div class="shop-info">
                <div style="font-weight: bold; font-size: 11pt; margin-bottom: 2mm;">とんかつ和とん</div>
                <div>〒510-0236 三重県鈴鹿市中江島町19-38</div>
                <div>TEL: 059-389-7787</div>
            </div>
            <div class="receipt-info">
                <div>発行日: ${receiptDate.toLocaleDateString('ja-JP')}</div>
                <div>No. ${receiptNumber}</div>
            </div>
        </div>
    </div>
    <script>
        window.onload = function() {
            if (window.chrome) {
                setTimeout(function() {
                    window.print();
                    setTimeout(function() {
                        window.close();
                    }, 1000);
                }, 100);
            } else {
                window.print();
                window.onafterprint = function() {
                    window.close();
                };
            }
        };
    </script>
</body>
</html>
    `;
  };

  // 横型領収書（品目なし） - 80mm幅横向き
  const generateHorizontalReceiptHTML = () => {
    const receiptDate = transaction.timestamp ? new Date(transaction.timestamp) : new Date();
    const receiptNumber = transaction.id?.toString().padStart(8, '0') || '00000000';
    
    return `
<!DOCTYPE html>
<html lang="ja">
<head>
    <meta charset="UTF-8">
    <title>領収書 - とんかつ和とん</title>
    <style>
        @page {
            size: 80mm 50mm;  /* 80mm幅、50mm高さ、横向き */
            margin: 0;
        }
        body {
            font-family: 'MS Gothic', monospace;
            margin: 0;
            padding: 0;
            font-size: 12px;
            line-height: 1.3;
            background: white;
        }
        .receipt-container {
            width: 80mm;
            height: 50mm;
            border: 2px solid black;
            padding: 3mm 4mm;
            box-sizing: border-box;
            position: relative;
        }
        
        /* ヘッダー */
        .header {
            text-align: center;
            margin-bottom: 3mm;
        }
        
        /* タイトル */
        .title {
            font-size: 18px;
            font-weight: bold;
            letter-spacing: 6px;
            margin-bottom: 2mm;
        }
        
        /* メインコンテンツエリア */
        .content-area {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3mm;
        }
        
        /* 左側セクション */
        .left-section {
            width: 60%;
        }
        
        /* 右側セクション */
        .right-section {
            width: 35%;
            text-align: right;
        }
        
        /* 宛名セクション */
        .addressee {
            font-size: 12px;
            margin-bottom: 3mm;
            border-bottom: 1px solid black;
            padding-bottom: 1mm;
            min-height: 5mm;
        }
        
        /* 金額セクション */
        .amount-section {
            margin-bottom: 3mm;
        }
        .amount-label {
            font-size: 11px;
            margin-bottom: 1mm;
        }
        .amount-box {
            border: 2px solid black;
            padding: 2mm;
            text-align: center;
            font-size: 16px;
            font-weight: bold;
            background: white;
        }
        
        /* 但し書き */
        .purpose {
            font-size: 11px;
            margin-bottom: 3mm;
        }
        
        /* 発行日 */
        .date-section {
            font-size: 11px;
            margin-bottom: 2mm;
        }
        
        /* 会社情報 */
        .company-info {
            text-align: right;
            font-size: 9px;
            line-height: 1.4;
        }
        .company-name {
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 1mm;
        }
        
        /* 印鑑スペース */
        .seal-area {
            position: absolute;
            bottom: 5mm;
            right: 5mm;
            width: 15mm;
            height: 15mm;
            border: 1px solid black;
            text-align: center;
            font-size: 10px;
            line-height: 15mm;
        }
        
        /* 領収書番号 */
        .receipt-number {
            display: none;  /* 番号は非表示 */
        }
        
        @media print {
            body {
                margin: 0;
                padding: 0;
            }
            .receipt-container {
                page-break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <div class="receipt-container">
        <div class="receipt-number">No. ${receiptNumber}</div>
        
        <!-- ヘッダー -->
        <div class="header">
            <div class="title">領 収 書</div>
        </div>
        
        <!-- メインコンテンツエリア -->
        <div class="content-area">
            <!-- 左側セクション -->
            <div class="left-section">
                <!-- 宛名 -->
                <div class="addressee">
                    ${customerName || '　　　　　　　　　　'}　様
                </div>
                
                <!-- 金額 -->
                <div class="amount-section">
                    <div class="amount-label">金額</div>
                    <div class="amount-box">
                        ￥ ${(transaction.total || 0).toLocaleString()}
                    </div>
                </div>
                
                <!-- 但し書き -->
                <div class="purpose">
                    但し、お食事代として
                </div>
                
                <!-- 発行日 -->
                <div class="date-section">
                    発行日：${receiptDate.toLocaleDateString('ja-JP', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                    })}
                </div>
            </div>
            
            <!-- 右側セクション -->
            <div class="right-section">
                <div class="company-info">
                    <div class="company-name">とんかつ和とん</div>
                    <div>〒510-XXXX</div>
                    <div>三重県鈴鹿市白子駅前通り</div>
                    <div>TEL：059-XXX-XXXX</div>
                </div>
            </div>
        </div>
        
        <!-- 印鑑エリア -->
        <div class="seal-area">
            印
        </div>
    </div>
</body>
</html>
    `;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            領収書発行
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              宛名（任意）
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="お客様名または会社名"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              ※空欄の場合は「　　　　　様」と印刷されます
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-sm text-gray-700 mb-3">領収内容</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">合計金額</span>
                <span className="font-bold">{formatCurrency(transaction.total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">支払方法</span>
                <span>{transaction.payment_type === 'cash' ? '現金' : (transaction.payment_type === 'paypay' || transaction.payment_type === 'PayPay' ? 'PayPay' : transaction.payment_type)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">発行日時</span>
                <span>{formatDateTime(transaction.timestamp)}</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              キャンセル
            </button>
            <button
              onClick={handlePrintReceipt}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FileText className="h-5 w-5" />
              <span>領収書を印刷</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
